import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { formatPathSegments } from 'components/FieldColumns/columnHeader';
import ColumnAttributeControl from './ColumnAttributeControl';

/**
 * Single selected column item with drag handle, hierarchy, title, and remove button.
 * Supports both internal reorder drag and external field drop.
 */
var SelectedColumnItem = function(props) {
  var field = props.field;
  var index = props.index;
  var onRemove = props.onRemove;
  var onDragStart = props.onDragStart;
  var onDragOver = props.onDragOver;
  var onDrop = props.onDrop;
  var onDragEnd = props.onDragEnd;
  var isDragging = props.isDragging;
  var dragOverIndex = props.dragOverIndex;
  var dragOverSide = props.dragOverSide;
  var isExternalDragging = props.isExternalDragging;
  var onExternalDragOver = props.onExternalDragOver;
  var onExternalDrop = props.onExternalDrop;
  var fields = props.fields;
  var isRecordListView = props.isRecordListView;
  var onAttributeChange = props.onAttributeChange;
  var t = props.t;

  var handleDragStart = function(e) {
    if (field.locked) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    if (onDragStart) {
      onDragStart(index);
    }
  };

  var handleDragOver = function(e) {
    e.preventDefault();
    var rect = e.currentTarget.getBoundingClientRect();
    var y = e.clientY - rect.top;
    var side = y < rect.height / 2 ? 'top' : 'bottom';

    if (isExternalDragging) {
      e.dataTransfer.dropEffect = 'copy';
      if (onExternalDragOver) {
        onExternalDragOver(index, side);
      }
    } else {
      if (onDragOver) {
        onDragOver(index, side);
      }
    }
  };

  var handleDragLeave = function() {
    if (isExternalDragging && onExternalDragOver) {
      onExternalDragOver(null, null);
    } else if (onDragOver) {
      onDragOver(null, null);
    }
  };

  var handleDrop = function(e) {
    e.preventDefault();
    e.stopPropagation();
    var rect = e.currentTarget.getBoundingClientRect();
    var y = e.clientY - rect.top;
    var dropOnTop = y < rect.height / 2;

    if (isExternalDragging) {
      try {
        var fieldData = JSON.parse(e.dataTransfer.getData('application/json'));
        if (fieldData && onExternalDrop) {
          var insertIndex = dropOnTop ? index : index + 1;
          onExternalDrop(fieldData, insertIndex);
        }
      } catch (err) {
        // Invalid data
      }
    } else {
      var fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
      if (onDrop && !isNaN(fromIndex)) {
        var toIndex = dropOnTop ? index : index + 1;
        if (fromIndex < index) {
          toIndex = toIndex - 1;
        }
        onDrop(fromIndex, toIndex);
      }
    }
  };

  var handleDragEnd = function() {
    if (onDragEnd) {
      onDragEnd();
    }
  };

  var handleRemoveClick = function(e) {
    e.stopPropagation();
    e.preventDefault();
    if (onRemove) {
      onRemove(field.cuuid);
    }
  };

  // Show indicators for both internal and external drag
  var activeOverIndex = isExternalDragging ? props.externalDragOverIndex : dragOverIndex;
  var activeOverSide = isExternalDragging ? props.externalDragOverSide : dragOverSide;
  var showTopIndicator = activeOverIndex === index && activeOverSide === 'top' && !isDragging;
  var showBottomIndicator = activeOverIndex === index && activeOverSide === 'bottom' && !isDragging;

  return (
    <div
      className={classnames('selected-column-item', {
        'selected-column-item--locked': field.locked,
        'selected-column-item--dragging': isDragging,
        'selected-column-item--drop-top': showTopIndicator,
        'selected-column-item--drop-bottom': showBottomIndicator,
      })}
      draggable={!isExternalDragging && !field.locked}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
    >
      {field.locked ? (
        <div className="selected-column-item-lock">
          <span className="material-symbols-outlined">lock</span>
        </div>
      ) : (
        <div className="selected-column-item-drag-handle">
          <span className="material-symbols-outlined">drag_indicator</span>
        </div>
      )}
      <div className="selected-column-item-info">
        {field.hierarchyPath && field.hierarchyPath.length > 0 && !field.meta ? (
          <span className="selected-column-item-hierarchy">
            {formatPathSegments(field.hierarchyPath)}
          </span>
        ) : null}
        <span className="selected-column-item-title">{field.title}</span>
      </div>
      {!field.locked && !field.orphaned && (
        <ColumnAttributeControl
          field={field}
          fields={fields}
          isRecordListView={isRecordListView}
          onChange={onAttributeChange}
          t={t}
        />
      )}
      {!field.locked && (
        <button
          type="button"
          className="selected-column-item-remove"
          onClick={handleRemoveClick}
          onMouseDown={function(e) { e.stopPropagation(); }}
          draggable={false}
          aria-label={'Remove ' + field.title}
        >
          <i className="material-icons">close</i>
        </button>
      )}
    </div>
  );
};

SelectedColumnItem.propTypes = {
  field: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onRemove: PropTypes.func,
  onDragStart: PropTypes.func,
  onDragOver: PropTypes.func,
  onDrop: PropTypes.func,
  onDragEnd: PropTypes.func,
  isDragging: PropTypes.bool,
  dragOverIndex: PropTypes.number,
  dragOverSide: PropTypes.string,
  isExternalDragging: PropTypes.bool,
  externalDragOverIndex: PropTypes.number,
  externalDragOverSide: PropTypes.string,
  onExternalDragOver: PropTypes.func,
  onExternalDrop: PropTypes.func,
  isMobile: PropTypes.bool,
  fields: PropTypes.array,
  isRecordListView: PropTypes.bool,
  onAttributeChange: PropTypes.func,
  t: PropTypes.func,
};

/**
 * Vertical list of selected columns with drag-to-reorder and external field drop
 */
var SelectedColumnsList = function(props) {
  var fields = props.fields;
  var onRemove = props.onRemove;
  var onReorder = props.onReorder;
  var isExternalDragging = props.isExternalDragging;
  var onExternalDrop = props.onExternalDrop;
  var isMobile = props.isMobile;
  var t = props.t;

  // Internal reorder state
  var _draggingState = useState(null);
  var draggingIndex = _draggingState[0];
  var setDraggingIndex = _draggingState[1];

  var _dragOverState = useState(null);
  var dragOverIndex = _dragOverState[0];
  var setDragOverIndex = _dragOverState[1];

  var _dragOverSideState = useState(null);
  var dragOverSide = _dragOverSideState[0];
  var setDragOverSide = _dragOverSideState[1];

  // External drag state (separate from internal reorder)
  var _extDragOverState = useState(null);
  var extDragOverIndex = _extDragOverState[0];
  var setExtDragOverIndex = _extDragOverState[1];

  var _extDragOverSideState = useState(null);
  var extDragOverSide = _extDragOverSideState[0];
  var setExtDragOverSide = _extDragOverSideState[1];

  var handleDragStart = function(index) {
    setDraggingIndex(index);
  };

  var handleDragOver = function(index, side) {
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  var handleDrop = function(fromIndex, toIndex) {
    if (fromIndex !== toIndex && onReorder) {
      onReorder(fromIndex, toIndex);
    }
    setDraggingIndex(null);
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  var handleDragEnd = function() {
    setDraggingIndex(null);
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  var handleExternalDragOver = function(index, side) {
    setExtDragOverIndex(index);
    setExtDragOverSide(side);
  };

  var handleExternalDrop = function(fieldData, insertIndex) {
    if (onExternalDrop) {
      onExternalDrop(fieldData, insertIndex);
    }
    setExtDragOverIndex(null);
    setExtDragOverSide(null);
  };

  // Fallback drop handler for the list container (drops on empty space around items)
  var handleListDragOver = function(e) {
    e.preventDefault();
    if (isExternalDragging) {
      e.dataTransfer.dropEffect = 'copy';
      // When hovering directly on the container (not a child item),
      // determine if cursor is above the first item or below the last
      if (e.target === e.currentTarget && fields && fields.length > 0) {
        var firstChild = e.currentTarget.firstElementChild;
        if (firstChild && e.clientY < firstChild.getBoundingClientRect().top) {
          setExtDragOverIndex(0);
          setExtDragOverSide('top');
        } else {
          setExtDragOverIndex(fields.length - 1);
          setExtDragOverSide('bottom');
        }
      }
    }
  };

  var handleListDrop = function(e) {
    e.preventDefault();
    if (isExternalDragging) {
      try {
        var fieldData = JSON.parse(e.dataTransfer.getData('application/json'));
        if (fieldData && onExternalDrop) {
          // Check if dropping above the first item or below the last
          var firstChild = e.currentTarget.firstElementChild;
          var insertAtStart = firstChild && e.clientY < firstChild.getBoundingClientRect().top;
          onExternalDrop(fieldData, insertAtStart ? 0 : fields.length);
        }
      } catch (err) {
        // Invalid data
      }
    }
    setExtDragOverIndex(null);
    setExtDragOverSide(null);
  };

  var handleListDragLeave = function(e) {
    // Only reset if leaving the list entirely (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setExtDragOverIndex(null);
      setExtDragOverSide(null);
    }
  };

  // Empty state drop handlers (same pattern as old PreviewTablePicker empty state)
  var handleEmptyDragOver = function(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  var handleEmptyDrop = function(e) {
    e.preventDefault();
    try {
      var fieldData = JSON.parse(e.dataTransfer.getData('application/json'));
      if (fieldData && onExternalDrop) {
        onExternalDrop(fieldData, 0);
      }
    } catch (err) {
      // Invalid data
    }
  };

  if (!fields || fields.length === 0) {
    return (
      <div
        className={classnames('selected-columns-empty', {
          'selected-columns-empty--drag-over': isExternalDragging,
        })}
        onDragOver={isExternalDragging ? handleEmptyDragOver : undefined}
        onDrop={isExternalDragging ? handleEmptyDrop : undefined}
      >
        <span className="material-symbols-outlined selected-columns-empty-icon">view_column</span>
        <p className="selected-columns-empty-text">
          {isExternalDragging ? t('settings:dropColumnHere') : t('settings:noColumnsSelected')}
        </p>
      </div>
    );
  }

  return (
    <div
      className="selected-columns-list"
      onDragOver={isExternalDragging ? handleListDragOver : undefined}
      onDrop={isExternalDragging ? handleListDrop : undefined}
      onDragLeave={isExternalDragging ? handleListDragLeave : undefined}
    >
      {fields.map(function(field, index) {
        return (
          <SelectedColumnItem
            key={field.cuuid}
            field={field}
            index={index}
            onRemove={onRemove}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
            isDragging={draggingIndex === index}
            dragOverIndex={dragOverIndex}
            dragOverSide={dragOverSide}
            isExternalDragging={isExternalDragging}
            externalDragOverIndex={extDragOverIndex}
            externalDragOverSide={extDragOverSide}
            onExternalDragOver={handleExternalDragOver}
            onExternalDrop={handleExternalDrop}
            isMobile={isMobile}
            fields={fields}
            isRecordListView={props.isRecordListView}
            onAttributeChange={props.onAttributeChange}
            t={props.t}
          />
        );
      })}
    </div>
  );
};

SelectedColumnsList.propTypes = {
  fields: PropTypes.array.isRequired,
  onRemove: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  isExternalDragging: PropTypes.bool,
  onExternalDrop: PropTypes.func,
  isMobile: PropTypes.bool,
  isRecordListView: PropTypes.bool,
  onAttributeChange: PropTypes.func,
  t: PropTypes.func.isRequired,
};

export default SelectedColumnsList;
